import { useEffect, useRef, useState } from "react";
import { observer } from "mobx-react-lite";
import { PageBox } from "@/components/UI/page";
import { ActionHandler } from "@/components/UI/tools";
import { appState } from "@/app/appState";
import { listLlmModels, LlmModelOut } from "@/api";
import {
    ActiveToggle,
    EvaluationHeader,
    InactiveBadge,
    JudgesTab,
    MasterDetail,
    RankingView,
    ResultsTab,
    TestsTab,
    VoteTab,
    makeEvalApi,
    makeSetActive,
    reportError,
} from "@/app/workspace/evaluation/evaluationShared";

// ---------------------------------------------------------------------------
// Types (mirror backend schemas.summarisation_test)
// ---------------------------------------------------------------------------

type Prompt = {
    id: number;
    organisation_id: number;
    name: string | null;
    chunk_compressor_prompt: string | null;
    synthesize_summary_prompt: string | null;
    active: boolean;
};

type EventRow = {
    id: number;
    organisation_id: number;
    event_id: number;
    event_name: string | null;
    has_transcription: boolean;
    active: boolean;
};

type EventRankings = {
    summarisation_test_event_id: number;
    event_id: number;
    rankings: RankingView;
};

type SummariserJudgeResults = {
    judge: string;
    judge_label: string;
    kind: string; // "agent" | "human" | "combined"
    overall: RankingView;
    per_event: EventRankings[];
    match_count: number;
};

type ModelSet = {
    id: number;
    organisation_id: number;
    chunk_compressor_llm_model_id: number;
    synthesiser_llm_model_id: number;
    chunk_compressor_key: string | null;
    synthesiser_key: string | null;
    active: boolean;
};

type Progress = {
    prompt_count: number;
    event_count: number;
    model_count: number;
    expected_summaries: number;
    completed_summaries: number;
};

const TABS = [
    "prompts",
    "events",
    "models",
    "tests",
    "judges",
    "vote",
    "results",
] as const;

type Tab = (typeof TABS)[number];

const api = makeEvalApi("/api/summarisation-tests");

const promptLabel = (p: Prompt) => p.name?.trim() || `Prompt #${p.id}`;

const modelSetLabel = (m: ModelSet) =>
    `${m.chunk_compressor_key ?? "?"} → ${m.synthesiser_key ?? "?"}`;

/** Disabled competitors stay in the historical rankings, so mark them there. */
const withActiveSuffix = (label: string, active: boolean) =>
    active ? label : `${label} (off)`;

/** Models enabled in Settings → Models. */
async function fetchLlmModels(): Promise<LlmModelOut[]> {
    const response = await listLlmModels({ body: { enabled_only: true } });
    if (response.error) throw new Error("Failed to load LLM models");
    return response.data ?? [];
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export const SummarisationTestsPage = observer(() => {
    const isSuperAdmin = appState.user?.is_super_admin;
    const [tab, setTab] = useState<Tab>("prompts");

    if (!isSuperAdmin) {
        return (
            <PageBox>
                <p>{appState.t("settings.unauthorized")}</p>
            </PageBox>
        );
    }

    return (
        <PageBox>
            <div className="flex flex-col gap-2 h-[calc(100vh-8rem)] min-h-[24rem]">
                <EvaluationHeader
                    route="summarisation-tests"
                    tabs={TABS}
                    tab={tab}
                    setTab={setTab}
                />

                <div className="flex-1 min-h-0">
                    {tab === "prompts" && <PromptsTab />}
                    {tab === "events" && <EventsTab />}
                    {tab === "models" && <ModelsTab />}
                    {tab === "tests" && <TestsTab api={api} />}
                    {tab === "judges" && <JudgesTab api={api} />}
                    {tab === "vote" && <SummariserVoteTab />}
                    {tab === "results" && <SummariserResultsTab />}
                </div>
            </div>
        </PageBox>
    );
});

// ---------------------------------------------------------------------------
// Prompts tab — create + read-only view (no editing of saved content) + delete
// ---------------------------------------------------------------------------

const PromptsTab = observer(() => {
    const [rows, setRows] = useState<Prompt[]>([]);
    const [selectedId, setSelectedId] = useState<number | null>(null);
    const [isNew, setIsNew] = useState(false);
    const [draftName, setDraftName] = useState("");
    const [draftCompressor, setDraftCompressor] = useState("");
    const [draftSynth, setDraftSynth] = useState("");
    const [saving, setSaving] = useState(false);
    const setActive = makeSetActive(api, "prompt");

    const load = async (preserveId?: number) => {
        try {
            const data = await api<Prompt[]>("list-prompts", {});
            setRows(data);
            const target = preserveId ?? (data.length > 0 ? data[0].id : null);
            setSelectedId(target);
            setIsNew(false);
        } catch (e) {
            reportError(e);
        }
    };

    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const selected = rows.find((x) => x.id === selectedId) ?? null;

    const addNew = () => {
        setSelectedId(null);
        setIsNew(true);
        setDraftName("");
        setDraftCompressor("");
        setDraftSynth("");
    };

    const create = async () => {
        setSaving(true);
        try {
            const created = await api<Prompt>("create-prompt", {
                name: draftName,
                chunk_compressor_prompt: draftCompressor,
                synthesize_summary_prompt: draftSynth,
            });
            await load(created.id);
        } catch (e) {
            reportError(e);
        } finally {
            setSaving(false);
        }
    };

    const remove = async (id: number) => {
        setSaving(true);
        try {
            await api("delete-prompt", { id });
            await load();
        } catch (e) {
            reportError(e);
        } finally {
            setSaving(false);
        }
    };

    const toggleActive = async (p: Prompt) => {
        setSaving(true);
        try {
            await setActive(p.id, !p.active);
            await load(p.id);
        } catch (e) {
            reportError(e);
        } finally {
            setSaving(false);
        }
    };

    return (
        <MasterDetail
            addLabel="+ Add prompt set"
            onAdd={addNew}
            items={rows.map((p) => ({
                id: p.id,
                label: promptLabel(p),
                selected: !isNew && p.id === selectedId,
                active: p.active,
                onClick: () => {
                    setSelectedId(p.id);
                    setIsNew(false);
                },
            }))}
            emptyLabel="No prompt sets"
        >
            {isNew ? (
                <div className="flex flex-col gap-2 h-full min-h-0">
                    <p className="admin-note">
                        New prompt set — summaries precompute on save. Prompt content
                        cannot be edited once saved.
                    </p>
                    <label className="flex flex-col gap-1">
                        <span className="field-label">Name</span>
                        <input
                            className="input input-mono"
                            value={draftName}
                            onChange={(e) => setDraftName(e.target.value)}
                            placeholder={appState.t("eval.promptNamePlaceholder")}
                        />
                    </label>
                    <label className="flex flex-col gap-1 flex-1 min-h-0">
                        <span className="field-label">
                            Chunk compressor prompt
                        </span>
                        <textarea
                            className="textarea input-mono flex-1 min-h-[8rem]"
                            value={draftCompressor}
                            onChange={(e) => setDraftCompressor(e.target.value)}
                        />
                    </label>
                    <label className="flex flex-col gap-1 flex-1 min-h-0">
                        <span className="field-label">
                            Synthesize summary prompt
                        </span>
                        <textarea
                            className="textarea input-mono flex-1 min-h-[8rem]"
                            value={draftSynth}
                            onChange={(e) => setDraftSynth(e.target.value)}
                        />
                    </label>
                    <div className="flex flex-row gap-2 shrink-0">
                        <ActionHandler title={appState.t("common.save")} funq={create} disabled={saving} className="btn-ghost btn-mono">
                            {saving ? appState.t("common.saving") : appState.t("common.save")}
                        </ActionHandler>
                    </div>
                </div>
            ) : selected ? (
                <div className="flex flex-col gap-2 h-full min-h-0">
                    <div className="flex flex-row items-center justify-between gap-2">
                        <h2 className="admin-heading flex flex-row items-center gap-2 min-w-0">
                            <span className="truncate">{promptLabel(selected)}</span>
                            {!selected.active && <InactiveBadge />}
                        </h2>
                        <div className="flex flex-row items-center gap-2 shrink-0">
                            <ActiveToggle
                                active={selected.active}
                                onToggle={() => toggleActive(selected)}
                                disabled={saving}
                            />
                            <ActionHandler
                                title={appState.t("common.delete")}
                                funq={() => remove(selected.id)}
                                disabled={saving}
                                className="btn-ghost btn-mono"
                            >
                                {appState.t("common.delete")}
                            </ActionHandler>
                        </div>
                    </div>
                    {!selected.active && (
                        <p className="admin-note">
                            Disabled — no new summaries or matches are generated for this
                            prompt set. Existing ones are kept.
                        </p>
                    )}
                    <label className="flex flex-col gap-1 flex-1 min-h-0">
                        <span className="field-label">
                            Chunk compressor prompt
                        </span>
                        <textarea
                            readOnly
                            className="textarea input-mono flex-1 min-h-[8rem]"
                            value={selected.chunk_compressor_prompt ?? ""}
                        />
                    </label>
                    <label className="flex flex-col gap-1 flex-1 min-h-0">
                        <span className="field-label">
                            Synthesize summary prompt
                        </span>
                        <textarea
                            readOnly
                            className="textarea input-mono flex-1 min-h-[8rem]"
                            value={selected.synthesize_summary_prompt ?? ""}
                        />
                    </label>
                </div>
            ) : (
                <p className="empty-state">
                    Select a prompt set, or add a new one.
                </p>
            )}
        </MasterDetail>
    );
});

// ---------------------------------------------------------------------------
// Event picker — searchable, paginated dropdown backed by appState.getEvents
// ---------------------------------------------------------------------------

const EventPicker = observer(
    ({ onSelect, disabled }: { onSelect: (eventId: number) => void; disabled: boolean }) => {
        const [search, setSearch] = useState("");
        const [open, setOpen] = useState(false);
        const listRef = useRef<HTMLUListElement>(null);
        const containerRef = useRef<HTMLDivElement>(null);

        useEffect(() => {
            appState.getEvents({ pageNumber: 0, search });
            // eslint-disable-next-line react-hooks/exhaustive-deps
        }, [search]);

        // Close the dropdown when clicking anywhere outside the picker.
        useEffect(() => {
            if (!open) return;
            const onDown = (e: MouseEvent) => {
                if (!containerRef.current?.contains(e.target as Node)) {
                    setOpen(false);
                }
            };
            document.addEventListener("mousedown", onDown);
            return () => document.removeEventListener("mousedown", onDown);
        }, [open]);

        const onScroll = () => {
            const el = listRef.current;
            if (!el) return;
            if (el.scrollTop + el.clientHeight >= el.scrollHeight - 24) {
                appState.getEvents({ pageNumber: appState.events_page + 1, search });
            }
        };

        return (
            <div ref={containerRef} className="relative w-[18rem]">
                <input
                    className="input input-mono"
                    value={search}
                    disabled={disabled}
                    placeholder={appState.t("eval.searchEvents")}
                    onFocus={() => setOpen(true)}
                    onChange={(e) => {
                        setSearch(e.target.value);
                        setOpen(true);
                    }}
                />
                {open && (
                    <ul
                        ref={listRef}
                        onScroll={onScroll}
                        className="autocomplete"
                    >
                        {(appState.events ?? [])
                            .filter((ev) => ev.has_transcription)
                            .map((ev) => (
                                <li key={ev.id}>
                                    <button
                                        type="button"
                                        disabled={disabled}
                                        onClick={() => {
                                            onSelect(ev.id);
                                            setOpen(false);
                                            setSearch("");
                                        }}
                                        className="autocomplete-option truncate"
                                        title={ev.title}
                                    >
                                        #{ev.id} · {ev.title}
                                    </button>
                                </li>
                            ))}
                        {(appState.events ?? []).filter((ev) => ev.has_transcription).length === 0 && (
                            <li className="empty-state">
                                No matching events with a transcription
                            </li>
                        )}
                    </ul>
                )}
            </div>
        );
    }
);

// ---------------------------------------------------------------------------
// Events tab
// ---------------------------------------------------------------------------

const EventsTab = () => {
    const [rows, setRows] = useState<EventRow[]>([]);
    const [saving, setSaving] = useState(false);
    const setActive = makeSetActive(api, "context");

    const load = async () => {
        try {
            setRows(await api<EventRow[]>("list-events", {}));
        } catch (e) {
            reportError(e);
        }
    };

    useEffect(() => {
        load();
    }, []);

    const add = async (eventId: number) => {
        setSaving(true);
        try {
            await api<EventRow>("create-event", { event_id: eventId });
            await load();
        } catch (e) {
            reportError(e);
        } finally {
            setSaving(false);
        }
    };

    const remove = async (id: number) => {
        setSaving(true);
        try {
            await api("delete-event", { id });
            await load();
        } catch (e) {
            reportError(e);
        } finally {
            setSaving(false);
        }
    };

    const toggleActive = async (r: EventRow) => {
        setSaving(true);
        try {
            await setActive(r.id, !r.active);
            await load();
        } catch (e) {
            reportError(e);
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="panel gap-2 h-full">
            <div className="flex flex-row gap-2 items-end shrink-0">
                <label className="flex flex-col gap-1">
                    <span className="field-label">Add event to pool</span>
                    <EventPicker onSelect={add} disabled={saving} />
                </label>
            </div>
            <div className="flex-1 min-h-0 overflow-y-auto">
                <table className="data-table">
                    <thead>
                        <tr>
                            <th>Event</th>
                            <th>Title</th>
                            <th>Transcription</th>
                            <th></th>
                            <th></th>
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map((r) => (
                            <tr key={r.id} className={r.active ? "" : "data-table-row-inactive"}>
                                <td>
                                    <span className="flex flex-row items-center gap-1">
                                        #{r.event_id}
                                        {!r.active && <InactiveBadge />}
                                    </span>
                                </td>
                                <td className="truncate max-w-[20rem]">{r.event_name ?? "—"}</td>
                                <td>{r.has_transcription ? "yes" : "no"}</td>
                                <td className="text-right">
                                    <button
                                        type="button"
                                        disabled={saving}
                                        onClick={() => toggleActive(r)}
                                        className="link-muted"
                                    >
                                        {r.active ? "disable" : "enable"}
                                    </button>
                                </td>
                                <td className="text-right">
                                    <button
                                        type="button"
                                        onClick={() => remove(r.id)}
                                        className="link-danger"
                                    >
                                        remove
                                    </button>
                                </td>
                            </tr>
                        ))}
                        {rows.length === 0 && (
                            <tr>
                                <td colSpan={5} className="empty-state">
                                    No events in the pool
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

// ---------------------------------------------------------------------------
// Models tab — model sets (compressor + synthesiser LLM pairs)
// ---------------------------------------------------------------------------

const ModelsTab = observer(() => {
    const [rows, setRows] = useState<ModelSet[]>([]);
    const [models, setModels] = useState<LlmModelOut[]>([]);
    const [compressorId, setCompressorId] = useState<number | null>(null);
    const [synthId, setSynthId] = useState<number | null>(null);
    const [saving, setSaving] = useState(false);
    const setActive = makeSetActive(api, "model");

    const load = async () => {
        try {
            const [sets, llm] = await Promise.all([
                api<ModelSet[]>("list-models", {}),
                fetchLlmModels(),
            ]);
            setRows(sets);
            setModels(llm);
            if (llm.length > 0) {
                setCompressorId((c) => c ?? llm[0].id);
                setSynthId((s) => s ?? llm[0].id);
            }
        } catch (e) {
            reportError(e);
        }
    };

    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const add = async () => {
        if (compressorId == null || synthId == null) return;
        setSaving(true);
        try {
            await api<ModelSet>("create-model", {
                chunk_compressor_llm_model_id: compressorId,
                synthesiser_llm_model_id: synthId,
            });
            await load();
        } catch (e) {
            reportError(e);
        } finally {
            setSaving(false);
        }
    };

    const remove = async (id: number) => {
        setSaving(true);
        try {
            await api("delete-model", { id });
            await load();
        } catch (e) {
            reportError(e);
        } finally {
            setSaving(false);
        }
    };

    const toggleActive = async (m: ModelSet) => {
        setSaving(true);
        try {
            await setActive(m.id, !m.active);
            await load();
        } catch (e) {
            reportError(e);
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="panel gap-2 h-full">
            <p className="admin-note">
                A model set fixes the chunk-compressor and synthesiser models used to
                precompute summaries. Adding one multiplies the summary pool and
                triggers precompute. The implicit “production” baseline (current
                default models) always competes.
            </p>
            <div className="flex flex-row gap-2 items-end shrink-0 flex-wrap">
                <label className="flex flex-col gap-1">
                    <span className="field-label">Chunk compressor</span>
                    <select
                        className="select input-mono"
                        value={compressorId ?? ""}
                        onChange={(e) => setCompressorId(parseInt(e.target.value, 10))}
                    >
                        {models.map((m) => (
                            <option key={m.id} value={m.id}>{m.display_name}</option>
                        ))}
                    </select>
                </label>
                <label className="flex flex-col gap-1">
                    <span className="field-label">Synthesiser</span>
                    <select
                        className="select input-mono"
                        value={synthId ?? ""}
                        onChange={(e) => setSynthId(parseInt(e.target.value, 10))}
                    >
                        {models.map((m) => (
                            <option key={m.id} value={m.id}>{m.display_name}</option>
                        ))}
                    </select>
                </label>
                <ActionHandler title={appState.t("eval.addModelSet")} funq={add} disabled={saving} className="btn-ghost btn-mono">
                    {saving ? appState.t("common.saving") : appState.t("eval.addModelSet")}
                </ActionHandler>
            </div>
            <div className="flex-1 min-h-0 overflow-y-auto">
                <table className="data-table">
                    <thead>
                        <tr>
                            <th>#</th>
                            <th>Compressor</th>
                            <th>Synthesiser</th>
                            <th></th>
                            <th></th>
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map((m) => (
                            <tr key={m.id} className={m.active ? "" : "data-table-row-inactive"}>
                                <td>
                                    <span className="flex flex-row items-center gap-1">
                                        #{m.id}
                                        {!m.active && <InactiveBadge />}
                                    </span>
                                </td>
                                <td>{m.chunk_compressor_key ?? "?"}</td>
                                <td>{m.synthesiser_key ?? "?"}</td>
                                <td className="text-right">
                                    <button
                                        type="button"
                                        disabled={saving}
                                        onClick={() => toggleActive(m)}
                                        className="link-muted"
                                    >
                                        {m.active ? "disable" : "enable"}
                                    </button>
                                </td>
                                <td className="text-right">
                                    <button type="button" onClick={() => remove(m.id)} className="link-danger">
                                        remove
                                    </button>
                                </td>
                            </tr>
                        ))}
                        {rows.length === 0 && (
                            <tr>
                                <td colSpan={5} className="empty-state">
                                    No model sets — only the production baseline is used.
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );
});

// ---------------------------------------------------------------------------
// Vote + Results — shared components bound to summariser terminology
// ---------------------------------------------------------------------------

const SummariserVoteTab = () => (
    <VoteTab
        api={api}
        contextField="summarisation_test_event_id"
        outputNoun="Summary"
        describeContext={(pair) =>
            (pair.event_name as string | null) ?? `Event #${pair.event_id}`
        }
        renderOutput={(side) => String(side.summary ?? "")}
    />
);

const SummariserResultsTab = () => (
    <ResultsTab<SummariserJudgeResults, Progress>
        api={api}
        loadLabels={async () => {
            const [prompts, modelSets] = await Promise.all([
                api<Prompt[]>("list-prompts", {}),
                api<ModelSet[]>("list-models", {}),
            ]);
            return {
                promptNames: Object.fromEntries(
                    prompts.map((p) => [
                        p.id,
                        withActiveSuffix(promptLabel(p), p.active),
                    ])
                ),
                modelNames: Object.fromEntries(
                    modelSets.map((m) => [
                        m.id,
                        withActiveSuffix(modelSetLabel(m), m.active),
                    ])
                ),
            };
        }}
        perContext={(jr) =>
            jr.per_event.map((ev) => ({
                key: ev.summarisation_test_event_id,
                label: `Event #${ev.event_id}`,
                rankings: ev.rankings,
            }))
        }
        progressLine={(p, matchCount) =>
            `Summaries cached: ${p.completed_summaries}/${p.expected_summaries}` +
            ` (${p.prompt_count} prompts × ${p.event_count} events × ${p.model_count} model sets)` +
            (matchCount != null ? ` · matches: ${matchCount}` : "")
        }
    />
);
